import { ChangeDetectionStrategy, Component, computed, inject, output, ViewEncapsulation } from '@angular/core';
import { CREATOR_DICTIONARY, CreatorDictionary } from '@anothertrip/anothertrip-ds';

import { environment } from '../../../environments/environment.development';
import { OutreachInboxService } from '../../features/campaigns/services/outreach-inbox.service';
import { PlanUsageService } from '../../services/plan-usage/plan-usage.service';
import { DashboardNavItem, DashboardNavListComponent } from '../dashboard-nav-list/dashboard-nav-list.component';

@Component({
  standalone: true,
  selector: 'app-dashboard-nav',
  imports: [DashboardNavListComponent],
  templateUrl: './dashboard-nav.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'dashboard-nav-host',
  },
})
export class DashboardNavComponent {
  protected readonly dictionary = inject<CreatorDictionary>(CREATOR_DICTIONARY);
  private readonly outreachInbox = inject(OutreachInboxService);
  private readonly plan = inject(PlanUsageService);

  readonly itemClick = output<MouseEvent>();

  /**
   * Declared before the items, because they read it: a class field cannot reference one declared
   * below it. The plan meter already knows how many alerts are active, so this asks it rather than
   * fetching the watch list a second time.
   */
  private readonly isSweeping = computed<boolean>(() => (this.plan.usage()?.active ?? 0) > 0);

  // Which of these show a Beta pill is not set here: see `dashboard-nav-list/beta-nav.config.ts` (AT-2264).
  protected readonly dashboardItems: DashboardNavItem[] = [
    {
      label: 'Home',
      icon: 'home',
      routerLink: '/dashboard/home',
    },
    {
      label: 'Instagram Tools',
      children: [
        ...(environment.featureFlags.stats
          ? [
              {
                label: this.dictionary.nav.stats,
                icon: 'analytics',
                routerLink: '/dashboard/stats',
              },
            ]
          : []),
        {
          label: this.dictionary.nav.posts,
          icon: 'web_stories',
          routerLink: '/dashboard/posts',
        },
        // Auto-replies still exist, but per post, on the Posts page. The account-wide Automation page
        // is off behind `featureFlags.automation` pending removal — docs/AUTOMATION_FEATURE_REMOVAL.md.
        ...(environment.featureFlags.automation
          ? [
              {
                label: 'Automation',
                icon: 'manufacturing',
                routerLink: '/dashboard/automation',
              },
            ]
          : []),
      ],
    },
    {
      label: 'Collaborations',
      children: [
        {
          label: 'Campaigns',
          icon: 'campaign',
          routerLink: '/campaigns',
          // Stays on the child: DashboardNavParent carries no badge.
          badge: this.outreachInbox.totalUnread,
        },
        {
          // Next to Campaigns, because a brand is what a campaign belongs to: an agency picks the
          // business first and the campaign second.
          label: 'Brands',
          icon: 'storefront',
          routerLink: '/brands',
        },
        {
          // Named for what it searches: an operator looking up one creator picks "Creator Search",
          // which is the username lookup below, not this.
          label: 'Hashtag Search',
          icon: 'tag',
          routerLink: '/creator-discovery/search',
        },
        {
          // Looking up one creator is the obvious reading of "Creator Search", so it is this page.
          label: 'Creator Search',
          // `@` beside Hashtag Search's `#`: the icon shows what you type, the label what you find.
          icon: 'alternate_email',
          routerLink: '/creator-discovery/handle-search',
        },
        ...(environment.featureFlags.searchAssistance
          ? [
              {
                label: 'Search Assistance',
                icon: 'support_agent',
                routerLink: '/creator-discovery/search-assistance',
              },
            ]
          : []),
        {
          label: 'Search Alerts',
          icon: 'notifications_active',
          routerLink: '/creator-discovery/search-alerts',
          // The pulse is a claim that something is running, so it is made only when something is:
          // at least one alert is active and therefore sweeping. Paused alerts do not sweep.
          live: this.isSweeping,
        },
        {
          label: 'Saved Creators',
          icon: 'bookmark',
          routerLink: '/creator-discovery/my-creators',
        },
      ],
    },
  ];

  // The nav is the only thing left that needs the alert count — the sidebar's plan meter is gone —
  // so the fetch that feeds the pulse is asked for here.
  constructor() {
    this.plan.load();
  }
}
